import { useRef, type KeyboardEvent } from "react";
import { Monitor, Moon, Sun } from "lucide-react";
import { useTheme, type ThemePref } from "@/lib/theme";
import { cn } from "@/lib/format";

const OPTIONS: Array<{ value: ThemePref; label: string; icon: typeof Sun }> = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
];

/**
 * Light / dark / follow-the-OS switch. Also applies to the API reference.
 * A radio group: one tab stop (the checked option); arrows, Home and End move
 * and select.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const pref = useTheme((s) => s.pref);
  const setPref = useTheme((s) => s.setPref);
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);

  const select = (i: number) => {
    const n = OPTIONS.length;
    const next = ((i % n) + n) % n;
    setPref(OPTIONS[next]!.value);
    buttons.current[next]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const current = OPTIONS.findIndex((o) => o.value === pref);
    const moves: Record<string, number> = { ArrowRight: current + 1, ArrowDown: current + 1, ArrowLeft: current - 1, ArrowUp: current - 1, Home: 0, End: OPTIONS.length - 1 };
    if (!(e.key in moves)) return;
    e.preventDefault();
    select(moves[e.key]!);
  };

  return (
    <div role="radiogroup" aria-label="Theme" onKeyDown={onKeyDown} className={cn("inline-flex rounded-md border border-border bg-surface-2 p-0.5", className)}>
      {OPTIONS.map(({ value, label, icon: Icon }, i) => (
        <button
          key={value}
          ref={(el) => {
            buttons.current[i] = el;
          }}
          type="button"
          role="radio"
          aria-checked={pref === value}
          tabIndex={pref === value ? 0 : -1}
          title={`${label} theme`}
          onClick={() => setPref(value)}
          className={cn(
            "inline-flex h-7 flex-1 items-center justify-center gap-1.5 rounded px-2 text-xs font-medium transition-colors",
            pref === value ? "bg-primary/15 text-primary" : "text-subtle hover:text-fg",
          )}
        >
          <Icon className="size-3.5" aria-hidden />
          {label}
        </button>
      ))}
    </div>
  );
}
